import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Card, CardBody } from './ui/card.js';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Phase 5b — top-level React error boundary. Catches render-time and
 * lifecycle exceptions that would otherwise blank the page. Renders a
 * non-scary fallback with a reload button and surfaces the error
 * message so an operator (or the diagnostics page) can correlate it
 * with server logs.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced to the operator via the diagnostics ring buffer only
    // indirectly — for now we log to the browser console so a Sentry-
    // style remote sink can pick it up later without code changes.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ error: null });
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <Card>
          <CardBody>
            <div className="flex flex-col items-start gap-3">
              <h1 className="text-xl font-semibold text-danger">Something went wrong</h1>
              <p className="text-sm text-ink-muted">
                The interface hit an unexpected error and stopped rendering. Your data is safe —
                this only affected the browser tab.
              </p>
              <pre className="w-full overflow-auto rounded bg-surface-alt p-3 text-xs font-mono text-ink-muted">
                {this.state.error.message}
              </pre>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center px-4 py-2 rounded-md bg-accent text-white text-sm hover:opacity-90"
              >
                Reload page
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }
}
